import { initializeFirebase, isFirebaseAvailable } from './firebaseConfig';

interface FCMService {
  isSupported: () => boolean;
  requestPermission: () => Promise<boolean>;
  getToken: () => Promise<string | null>;
  subscribeToPushNotifications: () => Promise<string | null>;
  onMessageReceived: (callback: (payload: any) => void) => () => void;
  deleteToken: () => Promise<boolean>;
  clearStoredToken: () => void;
}

class FirebaseCloudMessagingService implements FCMService {
  private messaging: any = null;
  private getTokenFn: any = null;
  private onMessageFn: any = null;
  private isInitialized = false;
  private initializationPromise: Promise<boolean> | null = null;
  private subscriptionPromise: Promise<string | null> | null = null;

  constructor() {
    this.initializeService();
  }

  private async initializeService(): Promise<boolean> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<boolean> {
    try {
      // Check if Firebase is available
      const available = await isFirebaseAvailable();
      if (!available) {
        console.warn('Firebase is not available');
        return false;
      }

      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        console.warn('FCM service can only be used in browser environment');
        return false;
      }

      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        console.warn('Service workers are not supported in this browser');
        return false;
      }

      // Initialize Firebase and get messaging functions
      const { messaging, getToken, onMessage } = await initializeFirebase();
      
      if (!messaging) {
        console.warn('Firebase messaging not available');
        return false;
      }

      this.messaging = messaging;
      this.getTokenFn = getToken;
      this.onMessageFn = onMessage;
      this.isInitialized = true;

      console.log('FCM service initialized successfully');
      return true;
    } catch (error) {
      console.error('Error initializing FCM service:', error);
      return false;
    }
  }

  isSupported(): boolean {
    return typeof window !== 'undefined' && 
           'serviceWorker' in navigator && 
           'Notification' in window &&
           'PushManager' in window;
  }

  async requestPermission(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        console.warn('Push notifications are not supported');
        return false;
      }

      // Check current permission status
      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        console.warn('Push notifications are blocked by the user');
        return false;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  async getToken(): Promise<string | null> {
    try {
      await this.initializeService();
      
      if (!this.isInitialized || !this.messaging || !this.getTokenFn) {
        console.warn('FCM service is not properly initialized');
        return null;
      }

      // Check if we have permission
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.warn('No permission for push notifications');
        return null;
      }

      // Get the token
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      const token = await this.getTokenFn(this.messaging, {
        vapidKey: vapidKey || undefined
      });

      if (token) {
        console.log('FCM token obtained:', token);
        return token;
      } else {
        console.warn('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  async subscribeToPushNotifications(): Promise<string | null> {
    try {
      // Prevent multiple simultaneous subscription attempts
      if (this.subscriptionPromise) {
        console.log('FCM subscription already in progress, waiting...');
        return this.subscriptionPromise;
      }

      // Check if we already have a working subscription this session
      const sessionKey = 'fcm_initialized_' + (localStorage.getItem('userId') || 'anonymous');
      if (sessionStorage.getItem(sessionKey)) {
        const existingToken = localStorage.getItem('fcm_token');
        if (existingToken) {
          console.log('Using existing FCM token from this session');
          return existingToken;
        }
      }

      // Start the subscription process
      this.subscriptionPromise = this.performSubscription(sessionKey);
      const result = await this.subscriptionPromise;
      
      // Clear the promise after completion
      this.subscriptionPromise = null;
      
      return result;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      this.subscriptionPromise = null;
      return null;
    }
  }

  private async performSubscription(sessionKey: string): Promise<string | null> {
    console.log('Starting FCM subscription process...');
    
    // Generate new token
    const token = await this.getToken();
    if (!token) {
      return null;
    }

    // Send token to server
    const success = await this.sendTokenToServer(token);
    if (success) {
      // Store token locally and mark session as initialized
      localStorage.setItem('fcm_token', token);
      sessionStorage.setItem(sessionKey, 'true');
      console.log('Successfully subscribed to push notifications');
      return token;
    } else {
      console.error('Failed to send token to server');
      return null;
    }
  }

  onMessageReceived(callback: (payload: any) => void): () => void {
    if (!this.isInitialized || !this.messaging || !this.onMessageFn) {
      console.warn('FCM service is not properly initialized');
      return () => {}; // Return empty unsubscribe function
    }

    try {
      // Listen for foreground messages
      const unsubscribe = this.onMessageFn(this.messaging, (payload: any) => {
        console.log('Message received in foreground:', payload);
        callback(payload);
      });

      // Also listen for service worker messages
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
            // Handle notification click from service worker
            console.log('Notification click from service worker:', event.data);
            // Navigate to the URL if needed
            if (event.data.url && event.data.url !== '/') {
              window.location.href = event.data.url;
            }
          }
        });
      }

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up message listener:', error);
      return () => {};
    }
  }

  private async sendTokenToServer(token: string): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/fcm-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        console.log('FCM token sent to server successfully');
        return true;
      } else {
        console.error('Failed to send FCM token to server:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('Error sending FCM token to server:', error);
      return false;
    }
  }



  async deleteToken(): Promise<boolean> {
    try {
      await this.initializeService();
      
      if (!this.isInitialized || !this.messaging) {
        return false;
      }

      // Get the current token before deleting it
      const currentToken = localStorage.getItem('fcm_token');

      // Delete the token from Firebase
      const { deleteToken } = await import('firebase/messaging');
      await deleteToken(this.messaging);

      // Clear stored token
      this.clearStoredToken();

      // Notify server to remove the token (if we had one)
      if (currentToken) {
        await fetch('/api/auth/fcm-token', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token: currentToken }),
        });
      }

      console.log('FCM token deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting FCM token:', error);
      return false;
    }
  }

  clearStoredToken(): void {
    // Remove FCM token from local storage
    localStorage.removeItem('fcm_token');
    console.log('FCM token cleared from localStorage');
  }
}

// Create and export a singleton instance
const fcmService = new FirebaseCloudMessagingService();

export default fcmService;
export type { FCMService }; 